const TYPE_SCALE_STORAGE_KEY = 'rp.typeScale'

export const TYPE_SCALES = ['xsmall', 'small', 'reduced', 'standard', 'large', 'xlarge'] as const
export type TypeScale = (typeof TYPE_SCALES)[number]

const DEFAULT_SCALE: TypeScale = 'standard'

const isTypeScale = (value: unknown): value is TypeScale =>
  typeof value === 'string' && (TYPE_SCALES as readonly string[]).includes(value)

const NO_STORAGE: Pick<Storage, 'getItem' | 'setItem'> = { getItem: () => null, setItem: () => undefined }

export const deviceStorage = (): Pick<Storage, 'getItem' | 'setItem'> => {
  try {
    return window.localStorage
  } catch {
    return NO_STORAGE
  }
}

export const readTypeScale = (storage: Pick<Storage, 'getItem'>): TypeScale => {
  try {
    const stored = storage.getItem(TYPE_SCALE_STORAGE_KEY)
    return isTypeScale(stored) ? stored : DEFAULT_SCALE
  } catch {
    return DEFAULT_SCALE
  }
}

export const storeTypeScale = (storage: Pick<Storage, 'setItem'>, scale: TypeScale): void => {
  try {
    storage.setItem(TYPE_SCALE_STORAGE_KEY, scale)
  } catch {
    return
  }
}

export const applyTypeScale = (root: { dataset: DOMStringMap }, scale: TypeScale): void => {
  if (scale === DEFAULT_SCALE) {
    delete root.dataset.typeScale
    return
  }
  root.dataset.typeScale = scale
}

type Listener = () => void

let current: TypeScale = DEFAULT_SCALE
let listeners: readonly Listener[] = []

export const initTypeScale = (root: { dataset: DOMStringMap }, storage: Pick<Storage, 'getItem'>): TypeScale => {
  current = readTypeScale(storage)
  applyTypeScale(root, current)
  return current
}

export const getTypeScale = (): TypeScale => current

export const subscribeTypeScale = (listener: Listener): (() => void) => {
  listeners = [...listeners, listener]
  return () => {
    listeners = listeners.filter((entry) => entry !== listener)
  }
}

export const setTypeScale = (
  root: { dataset: DOMStringMap },
  storage: Pick<Storage, 'setItem'>,
  scale: TypeScale,
): void => {
  current = scale
  applyTypeScale(root, scale)
  storeTypeScale(storage, scale)
  for (const listener of listeners) listener()
}
